import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Clock, CheckCircle, Eye, MousePointer, AlertTriangle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { useGetEmail } from '@/services/api/endpoints/EmailService';
import { formatDate } from '@/lib/utils';

const EMAIL_TYPE_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  email_verification: 'Verifikacija emaila',
  password_reset: 'Reset lozinke',
  password_changed: 'Lozinka promenjena',
};

interface TimelineEvent {
  label: string;
  timestamp?: string;
  icon: typeof Mail;
  color: string;
}

export function EmailDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: email, isLoading } = useGetEmail(id!);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {t('emails.notFound')}
      </div>
    );
  }

  const timelineEvents: TimelineEvent[] = [
    { label: t('emails.statusQueued'), timestamp: email.createdAt, icon: Clock, color: 'text-gray-500' },
    { label: t('emails.statusSent'), timestamp: email.sentAt, icon: Mail, color: 'text-blue-500' },
    { label: t('emails.statusDelivered'), timestamp: email.deliveredAt, icon: CheckCircle, color: 'text-green-500' },
    { label: t('emails.statusOpened'), timestamp: email.openedAt, icon: Eye, color: 'text-purple-500' },
    { label: t('emails.statusClicked'), timestamp: email.clickedAt, icon: MousePointer, color: 'text-indigo-500' },
  ];

  if (email.bouncedAt) {
    timelineEvents.push({ label: t('emails.statusBounced'), timestamp: email.bouncedAt, icon: AlertTriangle, color: 'text-orange-500' });
  }
  if (email.status === 'failed') {
    timelineEvents.push({ label: t('emails.statusFailed'), timestamp: email.updatedAt, icon: XCircle, color: 'text-red-500' });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/emails')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{t('emails.detail')}</h1>
          <p className="text-sm text-muted-foreground">{email.subject}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Info */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('emails.info')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground">{t('emails.recipient')}</p>
                <p className="text-sm font-medium">{email.to}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('emails.type')}</p>
                <p className="text-sm">{EMAIL_TYPE_LABELS[email.type] || email.type}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('common.status')}</p>
                <StatusBadge status={email.status} />
              </div>
              {email.resendId && (
                <div>
                  <p className="text-xs text-muted-foreground">Resend ID</p>
                  <p className="text-sm font-mono text-xs break-all">{email.resendId}</p>
                </div>
              )}
              {email.openCount > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('emails.opens')}</p>
                  <p className="text-sm font-medium">{email.openCount}</p>
                </div>
              )}
              {email.clickCount > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('emails.clicks')}</p>
                  <p className="text-sm font-medium">{email.clickCount}</p>
                </div>
              )}
              {email.errorMessage && (
                <div>
                  <p className="text-xs text-muted-foreground">{t('emails.error')}</p>
                  <p className="text-sm text-red-500">{email.errorMessage}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('emails.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {timelineEvents.filter(e => e.timestamp).map((event, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <event.icon className={`h-4 w-4 mt-0.5 ${event.color}`} />
                    <div>
                      <p className="text-sm font-medium">{event.label}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(event.timestamp!)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Email Preview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t('emails.preview')}</CardTitle>
          </CardHeader>
          <CardContent>
            {email.htmlContent ? (
              <iframe
                srcDoc={email.htmlContent}
                title="Email preview"
                className="w-full border rounded-lg bg-white"
                style={{ minHeight: '600px' }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                {t('emails.noPreview')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
